import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AgentService from "../../services/api/agents";
import { ACCESS_TOKEN, API_RESPONSE, APP_VERSION, planAction, projectType } from "../../settings/shared/constants";
import { showNotificationGlobal } from "../../components/common/NotificationProvider";
import TextareaInput from "../../components/agents/TextareaInput";
import { buildPlanRequestBody, isPlanAppType, writeComposerMode } from "../../components/agents/planUtils";
import waveSvg from "../../assets/img/wave.svg";
import TextRotator from '../../components/agents/TextRotator';
import { getYear } from "../../settings/shared/commonMethods";

const LOGIN_WAVES_CSS = `
  .sa-login-hero {
    background-color: #ffffff;
    overflow: hidden;
  }
  .sa-login-hero .sa-login-wave {
    position: absolute;
    left: 0%;
    right: 0;
    bottom: -37px;
    height: 320px;
    background-image: var(--sa-wave-svg);
    background-repeat: repeat-x;
    background-position: 0 100%;
    background-size: 1440px 320px;
    opacity: 0.65;
    z-index: 0;
    pointer-events: none;
  }
  @keyframes saLoginWaveDrift {
    from { transform: translate3d(0,0,0); }
    to   { transform: translate3d(260px,0,0); }
  }
  .sa-login-hero > * { position: relative; z-index: 1; }
  @media (prefers-reduced-motion: reduce) {
    .sa-login-hero .sa-login-wave { animation: none; }
  }
`;

const styles = {
    container: {
        minHeight: 'calc(100vh - 60px)',
        padding: '2rem',
        position: 'relative',
        zIndex: 1,
        fontFamily: "'DM Sans', sans-serif"
    },
    mainTitle: { animation: 'fadeInUp 0.8s ease-out' },
    heading: {
        fontSize: '3rem',
        fontWeight: '600',
        color: '#000',
        textShadow: '0 4px 8px rgba(0,0,0,0.1)',
        lineHeight: '1.1'
    },
    gradientText: {
        fontWeight: 'bold',
        // background: 'linear-gradient(to right, #E91E63, #9C27B0, #673AB7) text',
        background: 'linear-gradient(to right, rgb(106, 14, 191), rgba(99, 44, 213, 0.76), rgb(211, 95, 211)) text',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text'
    },
    generateGradientText: {
        fontWeight: 'bold',
        background: 'linear-gradient(to right, #000, #000, #673AB7) text',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text'
    },
    strikethrough: { textDecoration: 'line-through', textDecorationColor: 'rgb(106, 14, 191)', textDecorationThickness: '4px' },
    stepText: { fontSize: '2rem' }
};

const LoginChatView = ({ onSendClick }) => {
    const navigate = useNavigate();

    useEffect(() => {
        const id = "sa-login-waves-styles";
        if (!document.getElementById(id)) {
            const tag = document.createElement("style");
            tag.id = id;
            tag.textContent = LOGIN_WAVES_CSS;
            document.head.appendChild(tag);
        }
    }, []);

    useEffect(() => {
        if(localStorage.getItem(ACCESS_TOKEN)) {
            navigate('/apps');
        }
    }, [navigate]);

    const rotatingTexts = ['Agentic'];
    const handleChatSubmit = async (e, submitData) => {
        e.preventDefault();
        if (submitData) {
            // If onSendClick prop is provided, call it to show login form
            if (onSendClick) {
                onSendClick();
                return;
            }

            const formData = new FormData();
      
            const payload = isPlanAppType(submitData.appType)
                ? buildPlanRequestBody({ prompt: submitData.text, planAction: planAction.START })
                : { type: projectType.NEW, prompt: submitData.text, appType: submitData.appType };

            formData.append(
                "project",
                new Blob(
                    [JSON.stringify(payload)],
                    { type: "application/json" }
                )
            );

            const isXray = String(submitData.appType || "").toUpperCase() === "XRAY";
            if (!isXray) {
                submitData.uploadedFiles.forEach(item => {
                    formData.append("file", item.file);
                });
            }

            const response = await AgentService.createProject(formData);
            if (response?.status === API_RESPONSE.SUCCESS) {
                if (isPlanAppType(submitData.appType)) {
                    writeComposerMode(response.projectDTO.id, "PLAN");
                }
                navigate(`/project/${response.projectDTO.id}`);
            } else {
                showNotificationGlobal(response.errorMessage, 'error');
            }
        }
    };

    return (
        <>
        <div className="chat-container sa-login-hero d-flex flex-column align-items-center justify-content-center" style={styles.container}>
        <div className="sa-login-wave" aria-hidden="true" style={{ "--sa-wave-svg": `url(${waveSvg})` }} />
        <div className="main-title text-center mb-1" style={styles.mainTitle}>
            <h1 className="mb-4" style={styles.heading}>
                Build 
                  &nbsp;<TextRotator texts={rotatingTexts} period={2000}/>
                <span style={{ ...styles.gradientText, fontSize: '3.5rem' }}>Apps </span>in minutes
            </h1>
            <div className="steps">
                {['Generate', 'Preview', 'Ship'].map((step, idx) => (
                    <React.Fragment key={step}>
                        {idx > 0 && <div className="arrow"><span>➜</span></div>}
                        <div className="step">
                            <h3 style={{ ...styles.generateGradientText, ...styles.stepText }}>{step}</h3>
                        </div>
                    </React.Fragment>
                ))}
            </div>
        </div>

        <div className="chat-input-section">
            <TextareaInput onSubmit={handleChatSubmit} />
        </div>
    </div>
     <div style={{ position: 'absolute', bottom: '0px', left: '50%', transform: 'translateX(-50%)' }}>
     <span style={{ color: '#64748b', fontSize: '13px', fontWeight: '400' }}>
         © {getYear()} SuperAgentX • Version {APP_VERSION}
     </span>
 </div>
 </>
    );
};

export default LoginChatView;